import { useState } from 'react';
import { useDisclosure } from '@mantine/hooks';
import type { Participant, Session } from '../../../../types';
import type { SessionWithDetailsAndRsvp } from '../../../../services/api/sessions';
import type { CohortEventWithDetails } from '../../../../services/api/cohortEvents';

export interface UseSessionModalsReturn {
  // Export modal
  exportModalOpened: boolean;
  openExportModal: () => void;
  closeExportModal: () => void;

  // Edit session modal
  editModalOpened: boolean;
  openEditModal: () => void;
  closeEditModal: () => void;

  // Finalize modal
  finalizeModalOpened: boolean;
  openFinalizeModal: () => void;
  closeFinalizeModal: () => void;

  // Topic modal
  topicModalOpened: boolean;
  openTopicModal: () => void;
  closeTopicModal: () => void;

  // Huddle modal
  huddleModalOpened: boolean;
  openHuddleModal: () => void;
  closeHuddleModal: () => void;

  // Session detail modal
  sessionDetailModalOpened: boolean;
  openSessionDetailModal: () => void;
  closeSessionDetailModal: () => void;

  // Cohort event modals
  sendEventInvitesOpened: boolean;
  openSendEventInvites: () => void;
  closeSendEventInvites: () => void;

  editEventDateTimeOpened: boolean;
  openEditEventDateTime: () => void;
  closeEditEventDateTime: () => void;

  viewEventRsvpsOpened: boolean;
  openViewEventRsvps: () => void;
  closeViewEventRsvps: () => void;

  eventAttendanceOpened: boolean;
  openEventAttendance: () => void;
  closeEventAttendance: () => void;

  // Survey results modal
  surveyResultsModalOpened: boolean;
  openSurveyResultsModal: () => void;
  closeSurveyResultsModal: () => void;

  // Selected item state
  selectedSession: Session | null;
  setSelectedSession: (session: Session | null) => void;
  selectedSessionForTopic: SessionWithDetailsAndRsvp | null;
  setSelectedSessionForTopic: (session: SessionWithDetailsAndRsvp | null) => void;
  selectedCohortEvent: CohortEventWithDetails | null;
  setSelectedCohortEvent: (event: CohortEventWithDetails | null) => void;

  // Finalization state
  selectedGroupIds: string[] | undefined;
  setSelectedGroupIds: (ids: string[] | undefined) => void;
  finalizeMode: 'finalize' | 'resend';
  setFinalizeMode: (mode: 'finalize' | 'resend') => void;

  // Cohort event invites state
  sendEventInvitesMode: 'send' | 'resend';
  setSendEventInvitesMode: (mode: 'send' | 'resend') => void;

  // Survey results state
  surveyResultsGroupId: string | undefined;
  setSurveyResultsGroupId: (id: string | undefined) => void;
  surveyResultsGroupName: string | undefined;
  setSurveyResultsGroupName: (name: string | undefined) => void;
  surveyResultsCoachName: string | undefined;
  setSurveyResultsCoachName: (name: string | undefined) => void;
  surveyResultsInitialTab: 'post_session' | 'midpoint' | 'endpoint' | undefined;
  setSurveyResultsInitialTab: (tab: 'post_session' | 'midpoint' | 'endpoint' | undefined) => void;

  // Participant detail modal
  participantDetailOpened: boolean;
  openParticipantDetail: () => void;
  closeParticipantDetail: () => void;
  selectedParticipant: Participant | null;
  setSelectedParticipant: (participant: Participant | null) => void;
}

export function useSessionModals(): UseSessionModalsReturn {
  // Disclosure states
  const [exportModalOpened, { open: openExportModal, close: closeExportModal }] =
    useDisclosure(false);
  const [editModalOpened, { open: openEditModal, close: closeEditModal }] = useDisclosure(false);
  const [finalizeModalOpened, { open: openFinalizeModal, close: closeFinalizeModal }] =
    useDisclosure(false);
  const [topicModalOpened, { open: openTopicModal, close: closeTopicModal }] = useDisclosure(false);
  const [huddleModalOpened, { open: openHuddleModal, close: closeHuddleModal }] =
    useDisclosure(false);
  const [sessionDetailModalOpened, { open: openSessionDetailModal, close: closeSessionDetailModal }] =
    useDisclosure(false);
  const [sendEventInvitesOpened, { open: openSendEventInvites, close: closeSendEventInvites }] =
    useDisclosure(false);
  const [editEventDateTimeOpened, { open: openEditEventDateTime, close: closeEditEventDateTime }] =
    useDisclosure(false);
  const [viewEventRsvpsOpened, { open: openViewEventRsvps, close: closeViewEventRsvps }] =
    useDisclosure(false);
  const [eventAttendanceOpened, { open: openEventAttendance, close: closeEventAttendance }] =
    useDisclosure(false);
  const [surveyResultsModalOpened, { open: openSurveyResultsModal, close: closeSurveyResultsModal }] =
    useDisclosure(false);

  // Selected item state
  const [selectedSession, setSelectedSession] = useState<Session | null>(null);
  const [selectedSessionForTopic, setSelectedSessionForTopic] =
    useState<SessionWithDetailsAndRsvp | null>(null);
  const [selectedCohortEvent, setSelectedCohortEvent] = useState<CohortEventWithDetails | null>(null);

  // Finalization state
  const [selectedGroupIds, setSelectedGroupIds] = useState<string[] | undefined>(undefined);
  const [finalizeMode, setFinalizeMode] = useState<'finalize' | 'resend'>('finalize');

  // Cohort event invites state
  const [sendEventInvitesMode, setSendEventInvitesMode] = useState<'send' | 'resend'>('send');

  // Survey results state
  const [surveyResultsGroupId, setSurveyResultsGroupId] = useState<string | undefined>(undefined);
  const [surveyResultsGroupName, setSurveyResultsGroupName] = useState<string | undefined>(undefined);
  const [surveyResultsCoachName, setSurveyResultsCoachName] = useState<string | undefined>(undefined);
  const [surveyResultsInitialTab, setSurveyResultsInitialTab] = useState<'post_session' | 'midpoint' | 'endpoint' | undefined>(undefined);

  // Participant detail modal
  const [participantDetailOpened, { open: openParticipantDetail, close: closeParticipantDetail }] =
    useDisclosure(false);
  const [selectedParticipant, setSelectedParticipant] = useState<Participant | null>(null);

  return {
    exportModalOpened,
    openExportModal,
    closeExportModal,
    editModalOpened,
    openEditModal,
    closeEditModal,
    finalizeModalOpened,
    openFinalizeModal,
    closeFinalizeModal,
    topicModalOpened,
    openTopicModal,
    closeTopicModal,
    huddleModalOpened,
    openHuddleModal,
    closeHuddleModal,
    sessionDetailModalOpened,
    openSessionDetailModal,
    closeSessionDetailModal,
    sendEventInvitesOpened,
    openSendEventInvites,
    closeSendEventInvites,
    editEventDateTimeOpened,
    openEditEventDateTime,
    closeEditEventDateTime,
    viewEventRsvpsOpened,
    openViewEventRsvps,
    closeViewEventRsvps,
    eventAttendanceOpened,
    openEventAttendance,
    closeEventAttendance,
    surveyResultsModalOpened,
    openSurveyResultsModal,
    closeSurveyResultsModal,
    selectedSession,
    setSelectedSession,
    selectedSessionForTopic,
    setSelectedSessionForTopic,
    selectedCohortEvent,
    setSelectedCohortEvent,
    selectedGroupIds,
    setSelectedGroupIds,
    finalizeMode,
    setFinalizeMode,
    sendEventInvitesMode,
    setSendEventInvitesMode,
    surveyResultsGroupId,
    setSurveyResultsGroupId,
    surveyResultsGroupName,
    setSurveyResultsGroupName,
    surveyResultsCoachName,
    setSurveyResultsCoachName,
    surveyResultsInitialTab,
    setSurveyResultsInitialTab,
    participantDetailOpened,
    openParticipantDetail,
    closeParticipantDetail,
    selectedParticipant,
    setSelectedParticipant,
  };
}
